import React, { useState, useEffect } from "react";
import Avatar from "../assets/images/profile.png";
import { Link, useHistory } from "react-router-dom";
import { profilePath } from "../utils/profileRoutes";
import { Menu, Bell, User, LogOut } from "lucide-react";
import { logOut } from "../store/actions/authActions";
import { connect } from "react-redux";
import { compose } from "redux";
import { getProfile } from "../store/actions/profileActions";
import SearchBar from "../components/searchBar";
import { useTranslation } from "react-i18next";
import { useMobileNav } from "../context/MobileNavContext";
import axios from "../store/actions/axiosConfig";
import {
  AppHeader, HeaderGroup, HeaderTitle, HeaderIconButton,
  HeaderDropdown, HeaderDropdownSection, HeaderDropdownItem, HeaderDropdownTitle,
} from "./ui";

const API_URL = process.env.NODE_ENV === "production"
  ? "https://api.test.raborisk.ai"
  : "http://localhost:3000";

function formatRelativeTime(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const ACTION_LABELS = {
  RISK_ASSIGNED: "Assigned to Risk",
  RISK_ASSESSMENT_UPDATED: "Risk Edited",
  CONTROL_ASSIGNED: "Assigned to Control",
  CONTROL_UPDATED: "Control Updated",
  CONTROL_COMMENT_ADDED: "Comment Added",
  ACTION_ASSIGNED: "Assigned to Action",
  AUDIT_QUESTION_ASSIGNED: "Assigned to Audit Question",
  DOCUMENT_UPLOADED: "Document Uploaded",
  USER_PERMISSION_CHANGED: "Permission Changed",
};

function notifLabel(action_type) {
  return ACTION_LABELS[action_type] || action_type.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

function notifDetail(log) {
  try {
    const meta = typeof log.description === "string" ? JSON.parse(log.description || "{}") : {};
    const c = String(log.action_type || "");
    if (c === "RISK_ASSIGNED" || c === "RISK_ASSESSMENT_UPDATED") return meta.summary || meta.risk_title || "";
    if (c === "CONTROL_ASSIGNED" || c === "CONTROL_UPDATED") return meta.summary || meta.control_title || "";
    if (c === "CONTROL_COMMENT_ADDED") return meta.summary || "";
    if (c === "ACTION_ASSIGNED") return meta.title || "";
    if (c === "AUDIT_QUESTION_ASSIGNED") return meta.clause ? `Clause ${meta.clause}` : "";
    if (c === "DOCUMENT_UPLOADED") return meta.file_name || "";
    if (c === "USER_PERMISSION_CHANGED") return meta.new_role ? `→ ${meta.new_role}` : "";
    return "";
  } catch {
    return "";
  }
}

const TopBar = ({ logOutFN, getProfileFN, profile, notifSettings, className }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { isOpen: isNavOpen, toggleNav } = useMobileNav();

  const firstName = profile?.first_name || "";
  const lastName = profile?.last_name || "";
  const avatar = profile?.avatar || Avatar;

  const [profileOpen, setProfileOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState(null);
  const [notifLoading, setNotifLoading] = useState(false);
  const [dismissedIds, setDismissedIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("rabo_dismissed_notifs") || "[]")); }
    catch { return new Set(); }
  });

  const dismissNotif = (id) => {
    setDismissedIds(prev => {
      const next = new Set(prev);
      next.add(id);
      localStorage.setItem("rabo_dismissed_notifs", JSON.stringify([...next]));
      return next;
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest("[data-topbar-menu]")) {
        setProfileOpen(false);
        setShowNotifications(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleNotifications = async () => {
    setProfileOpen(false);
    setShowNotifications((s) => !s);
    if (notifications === null && profile?.user_id) {
      setNotifLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/log/notifications?limit=10`);
        setNotifications(res.data?.logs || []);
      } catch {
        setNotifications([]);
      } finally {
        setNotifLoading(false);
      }
    }
  };

  const toggleProfile = () => {
    setShowNotifications(false);
    setProfileOpen((prev) => !prev);
  };

  return (
    <AppHeader className={className}>
      <HeaderGroup className="min-w-0 gap-3">
        <HeaderIconButton
          aria-label={t("topBar.openMenu")}
          aria-expanded={isNavOpen}
          aria-haspopup="true"
          onClick={toggleNav}
          className="md:hidden flex-shrink-0"
        >
          <Menu className="w-5 h-5" />
        </HeaderIconButton>

        <HeaderTitle className="truncate">{t("app.name")}</HeaderTitle>
      </HeaderGroup>

      <div className="absolute left-1/2 -translate-x-1/2" data-topbar-menu>
        <SearchBar profile={profile} expandable alignRight />
      </div>

      <HeaderGroup className="flex-shrink-0 relative z-[1] gap-2" data-topbar-menu>
        <div className="relative">
          <HeaderIconButton
            aria-label={t("topBar.openNotifications")}
            aria-expanded={showNotifications}
            aria-haspopup="true"
            onClick={toggleNotifications}
            className="relative"
          >
            <Bell size={20} />
            {(() => {
              const visible = (notifications || []).filter(log =>
                !dismissedIds.has(log.log_id) &&
                (notifSettings == null || notifSettings.includes(log.action_type))
              );
              return (notifications === null || visible.length > 0)
                ? <span aria-hidden="true" className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
                : null;
            })()}
          </HeaderIconButton>

          {showNotifications && (
            <HeaderDropdown open align="right">
              <HeaderDropdownTitle>{t("topBar.notificationDropdown.title")}</HeaderDropdownTitle>
              <div className="divide-y divide-gray-100" role="list">
                {notifLoading ? (
                  <div className="px-4 py-3 text-xs text-gray-400">Loading…</div>
                ) : (() => {
                  const filtered = (notifications || []).filter(log =>
                    !dismissedIds.has(log.log_id) &&
                    (notifSettings == null || notifSettings.includes(log.action_type))
                  );
                  if (filtered.length === 0) {
                    return <div className="px-4 py-3 text-xs text-gray-400">No recent activity</div>;
                  }
                  return filtered.slice(0, 3).map((log) => {
                    const detail = notifDetail(log);
                    return (
                      <HeaderDropdownItem key={log.log_id} role="listitem">
                        <div className="flex items-start gap-2 py-0.5 w-full">
                          <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                            <span className="font-medium text-gray-800">{notifLabel(log.action_type)}</span>
                            {detail && <span className="text-gray-500 text-xs truncate max-w-[200px]">{detail}</span>}
                            <span className="text-gray-400 text-xs">{formatRelativeTime(log.created_at)}</span>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); dismissNotif(log.log_id); }}
                            className="text-gray-300 hover:text-gray-500 shrink-0 mt-0.5"
                            aria-label="Dismiss"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
                            </svg>
                          </button>
                        </div>
                      </HeaderDropdownItem>
                    );
                  });
                })()}
              </div>
              <div className="px-4 py-2 border-t border-gray-100" data-topbar-menu>
                <button
                  type="button"
                  className="text-xs text-gray-500 hover:text-gray-900 transition-colors"
                  onClick={() => { setShowNotifications(false); history.push("/notifications"); }}
                >
                  View all notifications →
                </button>
              </div>
            </HeaderDropdown>
          )}
        </div>

        <div className="relative">
          <button
            type="button"
            aria-label={t("topBar.openUserMenu")}
            aria-haspopup="true"
            aria-expanded={profileOpen}
            className="rounded-full p-0.5 hover:bg-gray-100 transition-colors focus-visible:ring-2 focus-visible:ring-blue-400 focus:outline-none"
            onClick={toggleProfile}
          >
            <img src={avatar || Avatar} alt={t("topBar.avatarAlt")} className="w-8 h-8 rounded-full" />
          </button>

          {profileOpen && (
            <HeaderDropdown open width="w-52" align="right">
              <HeaderDropdownSection bordered>
                <div className="flex items-center gap-2 px-4 py-3">
                  <img src={avatar || Avatar} alt={t("topBar.avatarAlt")} className="w-8 h-8 rounded-full flex-shrink-0" />
                  <span className="text-gray-900 truncate" style={{ fontSize: "0.83rem" }}>
                    {firstName} {lastName}
                  </span>
                </div>
              </HeaderDropdownSection>
              <HeaderDropdownSection>
                <HeaderDropdownItem as={Link} to={profilePath()}>
                  <User size={16} className="text-gray-400" aria-hidden="true" />
                  {t("topBar.editProfile")}
                </HeaderDropdownItem>
                <HeaderDropdownItem onClick={() => logOutFN()}>
                  <LogOut size={16} className="text-gray-400" aria-hidden="true" />
                  {t("topBar.logout")}
                </HeaderDropdownItem>
              </HeaderDropdownSection>
            </HeaderDropdown>
          )}
        </div>
      </HeaderGroup>
    </AppHeader>
  );
};

const mapStateToProps = (state) => ({
  profile: state.profile.profile,
  notifSettings: state.settings.settings?.notification_types || null,
});

const mapDispatchToProps = (dispatch) => ({
  logOutFN: () => dispatch(logOut()),
  getProfileFN: () => dispatch(getProfile()),
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(TopBar);
